import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import AdressInput from '../../Atoms/AdressInput';

const DesignEventForm = ({ initialData, isEditing, onSuccess, onError }) => {
    const [formData, setFormData] = useState({
        nom_etablissement: '',
        adresse: '',
        description_projet: '',
        site_web: '',
        event_type: 'Exhibition',
        design_events: 'Oui Design! 2025',
        date_debut_expo: '',
        date_fin_expo: '',
        vernissage_date: '',
        vernissage_hour: '',
        contact_rsvp: '',
        rsvp: '',
        instagram: '',
        tag: '',
        longitude: '',
        latitude: ''
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                longitude: initialData.longitude ? String(initialData.longitude) : '',
                latitude: initialData.latitude ? String(initialData.latitude) : ''
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Get form data including address input values
            const formDataObj = new FormData(e.target);
            const addressData = {
                adresse: formDataObj.get('adresse'),
                longitude: formDataObj.get('longitude'),
                latitude: formDataObj.get('latitude')
            };

            // Validate required fields
            if (!formData.nom_etablissement || !addressData.adresse) {
                throw new Error('Establishment name and address are required');
            }

            // Prepare data for submission
            const data = {
                ...formData,
                adresse: addressData.adresse,
                longitude: addressData.longitude ? addressData.longitude.toString() : null,
                latitude: addressData.latitude ? addressData.latitude.toString() : null,
                site_web: formData.site_web ? (
                    formData.site_web.startsWith('http') ? formData.site_web : `https://${formData.site_web}`
                ) : ''
            };

            if (isEditing && initialData?.id) {
                // Update existing event
                const { error } = await supabase
                    .from('itc_2025')
                    .update(data)
                    .eq('id', initialData.id);

                if (error) throw error;
            } else {
                // Create new event
                const { error } = await supabase
                    .from('itc_2025')
                    .insert([data]);

                if (error) throw error;
            }

            onSuccess();
            
            // Reset form if not editing
            if (!isEditing) {
                setFormData({
                    nom_etablissement: '',
                    adresse: '',
                    description_projet: '',
                    site_web: '',
                    event_type: 'Exhibition',
                    design_events: 'Oui Design! 2025',
                    date_debut_expo: '',
                    date_fin_expo: '',
                    vernissage_date: '',
                    vernissage_hour: '',
                    contact_rsvp: '',
                    rsvp: '',
                    instagram: '',
                    tag: '',
                    longitude: '',
                    latitude: ''
                });
                // Reset the form to clear AddressInput as well
                document.querySelector('form').reset();
            }
        } catch (error) {
            onError(error);
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = {
        width: '100%',
        padding: '8px 12px',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        fontSize: '14px',
        marginBottom: '16px'
    };

    const labelStyle = {
        display: 'block',
        marginBottom: '4px',
        fontWeight: '500',
        fontSize: '14px',
        color: '#333'
    };

    const textareaStyle = {
        ...inputStyle,
        minHeight: '100px',
        resize: 'vertical'
    };

    return (
        <form onSubmit={handleSubmit} style={{ maxWidth: '600px' }}>
            <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>
                    Establishment Name *
                    <input
                        type="text"
                        name="nom_etablissement"
                        value={formData.nom_etablissement}
                        onChange={handleChange}
                        required
                        style={inputStyle}
                        placeholder="Gallery, Museum, or Venue Name"
                    />
                </label>
            </div>

            <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>
                    Address *
                </label>
                <AdressInput
                    adressInputName="adresse"
                    latitutdeInputName="latitude"
                    longitudeInputName="longitude"
                    placeholder="Enter full address"
                    required={true}
                    adressDefaultValue={formData.adresse}
                    latitudeDefaultValue={formData.latitude}
                    longitudeDefaultValue={formData.longitude}
                />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                    <label style={labelStyle}>
                        Event Type
                        <select
                            name="event_type"
                            value={formData.event_type}
                            onChange={handleChange}
                            style={inputStyle}
                        >
                            <option value="Exhibition">Exhibition</option>
                            <option value="Workshop">Workshop</option>
                            <option value="Conference">Conference</option>
                            <option value="Opening">Opening</option>
                            <option value="Fair">Fair</option>
                            <option value="Other">Other</option>
                        </select>
                    </label>
                </div>
                <div>
                    <label style={labelStyle}>
                        Design Event Category
                        <select
                            name="design_events"
                            value={formData.design_events}
                            onChange={handleChange}
                            style={inputStyle}
                        >
                            <option value="Oui Design! 2025">Oui Design! 2025</option>
                            <option value="In the City">In the City</option>
                            <option value="Paris Design Week 2025">Paris Design Week 2025</option>
                        </select>
                    </label>
                </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>
                    Description
                    <textarea
                        name="description_projet"
                        value={formData.description_projet}
                        onChange={handleChange}
                        style={textareaStyle}
                        placeholder="Event description"
                    />
                </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                    <label style={labelStyle}>
                        Start Date
                        <input
                            type="date"
                            name="date_debut_expo"
                            value={formData.date_debut_expo}
                            onChange={handleChange}
                            style={inputStyle}
                        />
                    </label>
                </div>
                <div>
                    <label style={labelStyle}>
                        End Date
                        <input
                            type="date"
                            name="date_fin_expo"
                            value={formData.date_fin_expo}
                            onChange={handleChange}
                            style={inputStyle}
                        />
                    </label>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                    <label style={labelStyle}>
                        Vernissage Date
                        <input
                            type="date"
                            name="vernissage_date"
                            value={formData.vernissage_date}
                            onChange={handleChange}
                            style={inputStyle}
                        />
                    </label>
                </div>
                <div>
                    <label style={labelStyle}>
                        Vernissage Time
                        <input
                            type="text"
                            name="vernissage_hour"
                            value={formData.vernissage_hour}
                            onChange={handleChange}
                            style={inputStyle}
                            placeholder="e.g., 6pm-8pm"
                        />
                    </label>
                </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>
                    Website
                    <input
                        type="text"
                        name="site_web"
                        value={formData.site_web}
                        onChange={handleChange}
                        style={inputStyle}
                        placeholder="https://example.com"
                    />
                </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                    <label style={labelStyle}>
                        Instagram
                        <input
                            type="text"
                            name="instagram"
                            value={formData.instagram}
                            onChange={handleChange}
                            style={inputStyle}
                            placeholder="@instagram_handle"
                        />
                    </label>
                </div>
                <div>
                    <label style={labelStyle}>
                        Tags
                        <input
                            type="text"
                            name="tag"
                            value={formData.tag}
                            onChange={handleChange}
                            style={inputStyle}
                            placeholder="Custom tags"
                        />
                    </label>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                    <label style={labelStyle}>
                        RSVP Contact
                        <input
                            type="text"
                            name="contact_rsvp"
                            value={formData.contact_rsvp}
                            onChange={handleChange}
                            style={inputStyle}
                            placeholder="Email or phone"
                        />
                    </label>
                </div>
                <div>
                    <label style={labelStyle}>
                        RSVP Link
                        <input
                            type="text"
                            name="rsvp"
                            value={formData.rsvp}
                            onChange={handleChange}
                            style={inputStyle}
                            placeholder="RSVP URL"
                        />
                    </label>
                </div>
            </div>

            <button
                type="submit"
                disabled={loading}
                style={{
                    padding: '10px 20px',
                    backgroundColor: loading ? '#ccc' : '#0039d6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: '500',
                    width: '100%'
                }}
            >
                {loading ? 'Saving...' : (isEditing ? 'Update Design Event' : 'Add Design Event')}
            </button>
        </form>
    );
};

export default DesignEventForm;